/**
 * NotificationBell component - Iteration 6 - Notifications & Sharing.
 * 
 * Bell icon with badge showing unread notification count.
 */

import React, { useRef, useState } from 'react';
import { NotificationDropdown } from './NotificationDropdown';
import { useNotifications } from '../../hooks/useNotifications';
import { useLanguage } from '../../i18n/useLanguage';
import './NotificationBell.css';

export const NotificationBell: React.FC = () => {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const bellButtonRef = useRef<HTMLButtonElement>(null);
  const {
    notifications,
    unreadCount,
    isLoading,
    hasMore,
    loadMore,
    markAsRead,
    markAllAsRead,
    clearAll,
    refresh,
  } = useNotifications();

  const toggleDropdown = () => {
    setIsOpen(prev => !prev);
  };

  const closeDropdown = () => {
    setIsOpen(false);
  };

  return (
    <div className="notification-bell-container">
      <button 
        ref={bellButtonRef}
        className="notification-bell-btn"
        onClick={toggleDropdown}
        aria-label={
          unreadCount > 0
            ? t('notifications.bell.ariaWithUnread').replace('{count}', String(unreadCount))
            : t('notifications.bell.aria')
        }
        aria-expanded={isOpen}
      >
        <svg 
          className="notification-bell-icon" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
        <span className="notification-bell-label">{t('header.notifications')}</span>
      </button>

      {isOpen && (
        <NotificationDropdown
          anchorRef={bellButtonRef}
          notifications={notifications}
          isLoading={isLoading}
          hasMore={hasMore}
          onLoadMore={loadMore}
          onMarkAsRead={markAsRead}
          onMarkAllAsRead={markAllAsRead}
          onClearAll={clearAll}
          onClose={closeDropdown}
          onRefresh={refresh}
        />
      )}
    </div>
  );
};

export default NotificationBell;
